import filter from 'lodash/filter';
import reject from 'lodash/reject';
import type { GetterTree } from 'vuex';

import type { SelectOption } from '@emobg/web-components/loader';

import { compiledTranslation } from '@/utils/compiled-translation.util';
import type { AvailableBrands } from '@/utils/get-brand.util';
import { PREFERRED_COUNTRIES } from '@consts/locales.const';

import type { Country } from '../Broker/interfaces';
import type { State } from '../interfaces/state';
import type { AppState, CitiesState } from './interfaces';

export enum AppGetters {
  GetBrokerData = 'App:GET_BROKER_DATA',
  GetHeaders = 'App:GET_HEADERS',
  GetTranslation = 'App:GET_TRANSLATION',
  GetSortedCountries = 'App:GET_SORTED_COUNTRIES',
  GetProvinces = 'App:GET_PROVINCES',
  GetCities = 'App:GET_CITIES',
  GetBrand = 'App:GET_BRAND',
}

export interface AppGetterData {
  [AppGetters.GetBrokerData]: (state: AppState) => void;
  [AppGetters.GetHeaders]: (state: AppState) => void;
  [AppGetters.GetTranslation]: (state: AppState, translationKey: string) => void;
  [AppGetters.GetSortedCountries]: (state: AppState) => Country[];
  [AppGetters.GetProvinces]: (state: AppState) => SelectOption<string>[];
  [AppGetters.GetCities]: (state: AppState) => CitiesState;
  [AppGetters.GetBrand]: (state: AppState) => AvailableBrands;
}

export const getters: GetterTree<AppState, State> & AppGetterData = {
  [AppGetters.GetBrokerData](state) {
    return state.brokerData;
  },

  [AppGetters.GetHeaders](state) {
    const { brokerId, token } = state.brokerData;

    return {
      'Content-Type': 'application/json',
      'X-Broker-Language': state.translations.languageCode,
      'X-Broker-Id': brokerId,
      'X-Broker-Token': token,
    };
  },

  [AppGetters.GetTranslation]: (state) => (translationKey: string, params?: Record<string, string>) => {
    return compiledTranslation(state.translations, translationKey, params);
  },

  [AppGetters.GetSortedCountries](state) {
    const INDETERMINATE_CODE = '0';
    const countries = reject(state.countries, ({ code }) => code.trim() === INDETERMINATE_CODE);

    countries.sort((country1, country2) => country1.name.localeCompare(country2.name));

    const preferredCountries = filter(countries, ({ code }) => PREFERRED_COUNTRIES.includes(code));
    const otherCountries = reject(countries, ({ code }) => PREFERRED_COUNTRIES.includes(code));

    preferredCountries.sort((country1, country2) => PREFERRED_COUNTRIES.indexOf(country1.code) - PREFERRED_COUNTRIES.indexOf(country2.code));

    return [...preferredCountries, ...otherCountries];
  },
  [AppGetters.GetProvinces](state) {
    return state.provinces;
  },
  [AppGetters.GetCities](state): CitiesState {
    return state.cities || [];
  },
  [AppGetters.GetBrand](state) {
    return state.brand;
  },
} as const;
